import React from "react";
import { brandColors } from "@trusty/shared";

/**
 * Shared Haven onboarding brand tokens + small primitives, used by the auth screen
 * (AuthScreen) and the new-user dashboard carousel (OnboardingCarousel). Single source so
 * the redesign's palette, logo, and icons can't drift between the two surfaces.
 */
export const BRAND = {
  forest: brandColors.forest, // #042B1C
  mint: brandColors.mint, //    #A3E7AE
  elm: brandColors.elm, //      #1A7A82
  sage: "#EDF5EE",
  white: "#FFFFFF",
};

export const FONT_HEADING = "'Domine', Georgia, 'Times New Roman', serif";
export const FONT_BODY =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/** Shield-with-check glyph (the Haven mark). */
/**
 * A check in a circle: Haven's "checked / verified" mark. ENG-4034 (ENG-4025 decision 8): it was a
 * check in a shield (`ShieldCheck`), and the shield icon is retired with the word "Shield".
 */
export const CheckBadge: React.FC<{ color: string; size?: number; strokeWidth?: number }> = ({
  color,
  size = 20,
  strokeWidth = 2,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.2 l2.4 2.4 l4.6 -5" />
  </svg>
);

export const InfoIcon: React.FC<{ color: string; size?: number }> = ({ color, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11 v5" />
    <path d="M12 7.6 v0.01" />
  </svg>
);

/** The official multi-color Google "G". */
export const GoogleG: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

/** The official four-color Microsoft logo squares. */
export const MicrosoftLogo: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 23 23" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" fill="#F35325" />
    <rect x="12" y="1" width="10" height="10" fill="#81BC06" />
    <rect x="1" y="12" width="10" height="10" fill="#05A6F0" />
    <rect x="12" y="12" width="10" height="10" fill="#FFBA08" />
  </svg>
);

/** The Apple logo (monochrome), sized to sit alongside the Google/Microsoft marks. */
export const AppleLogo: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = "#000000" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M17.05 12.6c-.03-2.5 2.04-3.7 2.13-3.76-1.16-1.7-2.97-1.93-3.61-1.96-1.54-.16-3 .9-3.78.9-.78 0-1.98-.88-3.26-.86-1.68.03-3.23.98-4.09 2.48-1.74 3.03-.45 7.51 1.25 9.97.83 1.2 1.82 2.55 3.12 2.5 1.25-.05 1.72-.81 3.24-.81 1.5 0 1.94.81 3.26.78 1.35-.02 2.2-1.22 3.03-2.43.96-1.4 1.35-2.75 1.37-2.82-.03-.01-2.63-1.01-2.66-4.01zM14.6 5.1c.69-.84 1.16-2 1.03-3.16-1 .04-2.21.66-2.92 1.5-.64.74-1.2 1.93-1.05 3.06 1.11.09 2.25-.57 2.94-1.4z" />
  </svg>
);

/** Shared trust-line copy (sign-up screens) and trust-drawer copy (dashboard). */
export const TRUST_LINE = "Haven learns scam patterns, not your private browsing.";
export const TRUST_DRAWER = {
  title: "How Haven learns safely",
  paragraphs: [
    "Haven looks for scam patterns, not private browsing.",
    "When a new trick shows up, Haven can learn the structure of that trick — like a lookalike login, suspicious domain pattern, or fake support page.",
    "If that pattern shows up across enough people, it can help improve protection for everyone.",
  ],
  emphasis: ["Not the private URL.", "Not your browsing history.", "The pattern."],
  cta: "Got it",
};
